import { useEffect, useRef, useState } from 'react';
import type { Track } from 'livekit-client';
import { Permission, decodePermissions, has } from '@callzone/shared';
import {
  displayName,
  memberAccent,
  memberAvatarVersion,
  useActiveChannel,
  useActiveGuild,
  useAliases,
  useStore,
} from '../store';
import { voice } from '../lib/voice';
import { api } from '../lib/api';
import { screenShareUnavailableReason } from '../lib/platform';
import { useCelular } from '../lib/responsivo';
import { Avatar } from './Avatar';
import {
  CloseIcon,
  MicIcon,
  MicOffIcon,
  PhoneOffIcon,
  ScreenShareIcon,
  VideoIcon,
  VolumeIcon,
  VolumeOffIcon,
} from './icons';

function VideoSurface({
  track,
  muted,
  mirrored,
}: {
  track: Track;
  muted: boolean;
  mirrored?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [track]);

  return (
    <video ref={ref} className={mirrored ? 'mirrored' : ''} autoPlay playsInline muted={muted} />
  );
}

interface TileProps {
  identity: string;
  track?: Track;
  kind: 'camera' | 'screen' | 'avatar';
  isSelf: boolean;
  focused: boolean;
  onToggleFocus: () => void;
  canMute: boolean;
  serverMuted: boolean;
  onToggleMute: () => void;
  /** Só nos tiles de tela: deixa de renderizar aquela transmissão. */
  onEsconder?: () => void;
  /** Só para quem pode mover membros: desconecta a pessoa da call. */
  onTirarDaCall?: () => void;
}

function Tile({
  identity,
  track,
  kind,
  isSelf,
  focused,
  onToggleFocus,
  canMute,
  serverMuted,
  onToggleMute,
  onEsconder,
  onTirarDaCall,
}: TileProps) {
  const guild = useActiveGuild();
  const participant = useStore((s) => s.voiceParticipants[identity]);
  const celular = useCelular();
  useAliases();
  const name = displayName(guild, identity);

  return (
    <div
      className={`tile tile-${kind} ${participant?.speaking && kind === 'avatar' ? 'speaking' : ''} ${focused ? 'focused' : ''}`}
    >
      {track ? (
        // O proprio video sai mudo para nao causar eco. E espelhado: e assim
        // que a gente se ve num espelho, e ver a propria imagem "ao contrario"
        // desorienta. Vale so para a propria camera — a tela compartilhada
        // espelhada deixaria todo texto ilegivel, e o video dos outros deve
        // chegar como eles realmente estao.
        <VideoSurface track={track} muted={isSelf} mirrored={isSelf && kind === 'camera'} />
      ) : (
        <Avatar
          name={name}
          color={memberAccent(guild, identity)}
          size={celular ? 52 : 72}
          userId={identity}
          avatarVersion={memberAvatarVersion(guild, identity)}
        />
      )}

      <div className="tile-label">
        {participant?.micMuted && kind === 'avatar' && (
          <span title="Sem microfone">
            <MicOffIcon style={{ width: 13, height: 13 }} />
          </span>
        )}
        <span>
          {name}
          {kind === 'screen' && ' — tela'}
          {isSelf && kind !== 'screen' && ' (voce)'}
        </span>
        {participant?.connectionQuality === 'poor' && (
          <span title="Conexao instavel" style={{ color: 'var(--yellow)' }}>
            {'▲'}
          </span>
        )}
      </div>

      {/* Silenciar direto no tile: durante a call e onde a pessoa esta olhando. */}
      {canMute && !isSelf && kind !== 'screen' && (
        <button
          className={`tile-mute ${serverMuted ? 'on' : ''}`}
          title={serverMuted ? 'Liberar o microfone desta pessoa' : 'Silenciar esta pessoa'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleMute();
          }}
        >
          {serverMuted ? <MicOffIcon /> : <MicIcon />}
        </button>
      )}

      {kind !== 'avatar' && (
        <button className="expand" onClick={onToggleFocus}>
          {focused ? 'voltar a grade' : 'expandir'}
        </button>
      )}

      {/* Fechar a tela de outra pessoa: some da sua grade, so para voce. */}
      {kind === 'screen' && !isSelf && onEsconder && (
        <button className="tile-fechar" title="Nao assistir esta tela" onClick={onEsconder}>
          <CloseIcon />
        </button>
      )}

      {kind !== 'screen' && !isSelf && onTirarDaCall && (
        <button
          className="tile-kick"
          title="Tirar da call"
          onClick={(e) => {
            e.stopPropagation();
            onTirarDaCall();
          }}
        >
          <PhoneOffIcon />
        </button>
      )}
    </div>
  );
}

export function Stage() {
  const channel = useActiveChannel();
  const guild = useActiveGuild();
  const me = useStore((s) => s.me);
  const session = useStore((s) => s.voice);
  const voiceStates = useStore((s) => s.voiceStates);
  const tracks = useStore((s) => s.tracks);
  const patchVoice = useStore((s) => s.patchVoice);
  const toast = useStore((s) => s.toast);
  /*
   * Telas que voce escolheu nao assistir. E so seu e so nesta sessao: o SFU
   * continua enviando, mas parar de renderizar ja resolve o que incomoda, que
   * e a tela dos outros ocupando o espaco e pesando na maquina.
   */
  const [escondidas, setEscondidas] = useState<Set<string>>(new Set());
  useAliases();

  /*
   * NAO entrar automaticamente aqui. Ja houve uma versao que fazia isso e
   * quebrou a navegacao: o efeito reagia ao proprio estado que a conexao muda,
   * disparava um segundo `entrar` durante o primeiro, e a saida de um derrubava
   * a sala do outro ("Client initiated disconnect"). Pior: estando numa ligacao
   * privada, so olhar um canal de voz arrancava a pessoa de la.
   *
   * Quem entra e o clique na barra lateral, que acontece uma vez por acao.
   */

  if (!channel || !guild) return null;

  const connectedHere = session.channelId === channel.id && session.status !== 'idle';
  const occupants = Object.values(voiceStates).filter((v) => v.channelId === channel.id);

  if (!connectedHere) {
    /*
     * Clicar no canal na barra lateral ja entra. Esta tela e o que sobra: o
     * aviso enquanto conecta, o erro quando falha, e uma forma de voltar para
     * quem saiu da call mas continua olhando o canal.
     */
    return (
      <div className="stage">
        <div className="stage-empty">
          <div>
            <h2 style={{ marginBottom: 8 }}>{channel.name}</h2>
            <p style={{ marginBottom: 18 }}>
              {session.error
                ? 'Nao consegui conectar.'
                : session.status === 'connecting'
                  ? 'Entrando...'
                  : occupants.length === 0
                    ? 'Ninguem esta neste canal ainda.'
                    : `${occupants.length} ${occupants.length === 1 ? 'pessoa' : 'pessoas'} na call.`}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
              {occupants.map((state) => (
                <Avatar
                  key={state.userId}
                  name={displayName(guild, state.userId)}
                  color={memberAccent(guild, state.userId)}
                  size={44}
                />
              ))}
            </div>
            {session.error && (
              <div className="error-box" style={{ marginBottom: 16 }}>
                {session.error}
              </div>
            )}

            {/*
              Aparece so quando nao ha conexao em andamento. Enquanto conecta
              nao ha botao — era clicar nele durante a conexao que disparava a
              segunda entrada e derrubava a primeira.
            */}
            {session.status !== 'connecting' && (
              <button className="btn" onClick={() => void voice.join(guild.guild.id, channel.id)}>
                {session.error ? 'Tentar de novo' : 'Entrar no canal de voz'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Monta os tiles: uma tela compartilhada rende um tile proprio, alem do tile
  // da pessoa (camera ou avatar).
  const tiles: Array<{ key: string; identity: string; track?: Track; kind: 'camera' | 'screen' | 'avatar' }> = [];
  for (const state of occupants) {
    const participantTracks = tracks[state.userId];
    if (participantTracks?.screen && !escondidas.has(state.userId)) {
      tiles.push({
        key: `${state.userId}-screen`,
        identity: state.userId,
        track: participantTracks.screen,
        kind: 'screen',
      });
    }

    /*
     * O estado de voz do servidor manda sobre a faixa que o LiveKit entregou.
     * Ao desligar a camera o LiveKit as vezes so silencia a faixa em vez de
     * despublicar, e o evento de limpeza nao chega em todo navegador — quem
     * estava no celular continuava vendo um retangulo preto. O servidor sabe,
     * porque quem desligou avisou pelo gateway.
     */
    const cameraLigada = state.camera && !!participantTracks?.camera;
    tiles.push({
      key: `${state.userId}-self`,
      identity: state.userId,
      track: cameraLigada ? participantTracks?.camera : undefined,
      kind: cameraLigada ? 'camera' : 'avatar',
    });
  }

  const focusedKey = session.focusedIdentity;
  const visible = focusedKey ? tiles.filter((t) => t.key === focusedKey) : tiles;
  const canShare = session.canPublish.includes('screen_share');
  // Sem HTTPS o navegador nem oferece o seletor de tela; melhor avisar antes.
  const shareBlocked = canShare ? screenShareUnavailableReason() : null;
  const canCamera = session.canPublish.includes('camera');
  const canSpeak = session.canPublish.includes('microphone');
  const canMuteMembers = has(decodePermissions(guild.permissions), Permission.MUTE_MEMBERS);
  const canKickFromCall = has(decodePermissions(guild.permissions), Permission.MOVE_MEMBERS);

  /** Tira alguem da call sem expulsar da guild — so desconecta da voz. */
  async function tirarDaCall(userId: string) {
    const quem = displayName(guild, userId);
    if (!window.confirm(`Tirar ${quem} da call? A pessoa continua na guild.`)) return;
    try {
      await api.moderateVoice(guild!.guild.id, userId, { disconnect: true });
      toast(`${quem} saiu da call.`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nao consegui desconectar.', 'error');
    }
  }

  /** Silencia ou libera alguem no servidor — vale para todos, nao so para mim. */
  async function toggleServerMute(userId: string) {
    const atual = voiceStates[userId]?.serverMute ?? false;
    try {
      await api.moderateVoice(guild!.guild.id, userId, { serverMute: !atual });
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nao consegui silenciar.', 'error');
    }
  }

  return (
    <div className="stage">
      <div className={`stage-grid ${focusedKey ? 'focused' : ''}`}>
        {visible.map((tile) => (
          <Tile
            key={tile.key}
            identity={tile.identity}
            track={tile.track}
            kind={tile.kind}
            isSelf={tile.identity === me?.id}
            focused={focusedKey === tile.key}
            onToggleFocus={() =>
              patchVoice({ focusedIdentity: focusedKey === tile.key ? null : tile.key })
            }
            canMute={canMuteMembers}
            serverMuted={voiceStates[tile.identity]?.serverMute ?? false}
            onToggleMute={() => void toggleServerMute(tile.identity)}
            onEsconder={
              tile.kind === 'screen'
                ? () =>
                    setEscondidas((atual) => {
                      const proximo = new Set(atual);
                      proximo.add(tile.identity);
                      return proximo;
                    })
                : undefined
            }
            onTirarDaCall={canKickFromCall ? () => void tirarDaCall(tile.identity) : undefined}
          />
        ))}
      </div>

      {/* Sem isto, fechar a tela de alguem seria irreversivel ate sair da call. */}
      {escondidas.size > 0 && (
        <div className="telas-escondidas">
          {escondidas.size === 1 ? 'Uma tela oculta' : `${escondidas.size} telas ocultas`}
          <button className="btn ghost small" onClick={() => setEscondidas(new Set())}>
            Mostrar de novo
          </button>
        </div>
      )}

      <div className="voice-toolbar">
        <div className="status">
          <div className="line1">
            <span>{session.status === 'reconnecting' ? '◌' : '●'}</span>
            {session.status === 'reconnecting' ? 'Reconectando...' : 'Voz conectada'}
          </div>
          <div className="line2">
            {channel.name} · {occupants.length} {occupants.length === 1 ? 'pessoa' : 'pessoas'}
          </div>
        </div>

        <button
          className={`control-btn ${session.micEnabled ? '' : 'off'}`}
          title={canSpeak ? (session.micEnabled ? 'Desligar microfone' : 'Ligar microfone') : 'Sem permissao para falar'}
          disabled={!canSpeak}
          onClick={() => void voice.toggleMic()}
        >
          {session.micEnabled ? <MicIcon /> : <MicOffIcon />}
        </button>

        <button
          className={`control-btn ${session.deafened ? 'off' : ''}`}
          title={session.deafened ? 'Voltar a ouvir' : 'Mudo'}
          onClick={() => void voice.toggleDeafen()}
        >
          {session.deafened ? <VolumeOffIcon /> : <VolumeIcon />}
        </button>

        <button
          className={`control-btn ${session.cameraEnabled ? 'live' : ''}`}
          title={canCamera ? 'Camera' : 'Sem permissao para usar camera'}
          disabled={!canCamera}
          onClick={() => void voice.toggleCamera()}
        >
          <VideoIcon />
        </button>

        <button
          className={`control-btn ${session.sharingScreen ? 'live' : ''}`}
          title={
            shareBlocked ??
            (!canShare
              ? 'Sem permissao para compartilhar tela'
              : session.sharingScreen
                ? 'Parar de compartilhar'
                : 'Compartilhar tela')
          }
          disabled={!canShare || !!shareBlocked}
          onClick={() => {
            if (session.sharingScreen) void voice.stopScreenShare();
            else void voice.startScreenShare(true);
          }}
        >
          <ScreenShareIcon />
        </button>

        <button
          className="control-btn danger"
          title="Sair do canal"
          onClick={() => void voice.leave(true)}
        >
          <PhoneOffIcon />
        </button>
      </div>

    </div>
  );
}
